import velosLogoUrl from '@/assets/velos_icon_black_full.svg?url';
import velosSmallLogoUrl from '@/assets/velos_icon_black_small.svg?url';

import { Divider, Drawer, Flex, Group, Menu, Stack } from '@mantine/core';
import { useAuthInfo, type User } from '@propelauth/react';
import NavItem from '@/layouts/LeftSidebar/components/NavItem.tsx';
import UserMenu from './components/UserMenu';
import {
  HiOutlineClipboardDocumentCheck,
  HiOutlinePlus,
  HiOutlineRectangleGroup,
  HiOutlineSquaresPlus,
} from 'react-icons/hi2';
import CreateAutomationModal from '@/components/CreateAutomationModal/CreateAutomationModal.tsx';
import React, { ReactElement, useEffect, useState } from 'react';
import AppIcon from '@/components/AppIcon.tsx';
import { Link } from 'react-router-dom';
import StyledMenu from '@/components/StyledMenu/StyledMenu.tsx';
import ManualRunModal from '@/pages/AutomationPage/components/ManualRunModal/ManualRunModal.tsx';
import useRetrieveAutomation from '@/hooks/api/automations/useRetrieveAutomation.ts';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import { useDebouncedValue, useHover } from '@mantine/hooks';

type dataType = {
  link: string;
  label: string;
  Icon: ReactElement;
};

const data: dataType[] = [
  {
    link: '/automation-suites',
    label: 'Automation Suites',
    Icon: <AppIcon Icon={HiOutlineSquaresPlus} size={20} />,
  },
  {
    link: '/automations',
    label: 'Automations',
    Icon: <AppIcon Icon={HiOutlineRectangleGroup} size={20} />,
  },
  // {
  //   link: '/runs',
  //   label: 'Run History',
  //   Icon: <AppIcon Icon={HiOutlineRectangleStack} size={20} />,
  // },
  {
    link: '/integrations',
    label: 'Integrations',
    Icon: <AppIcon Icon={HiOutlineSquaresPlus} size={20} />,
  },
  {
    link: '/human-decisions',
    label: 'Human decisions',
    Icon: <AppIcon Icon={HiOutlineClipboardDocumentCheck} size={20} />,
  },
];

const PlusIcon = () => (
  <AppIcon Icon={HiOutlinePlus} size={'12px'} iconProps={{ strokeWidth: '2px' }} />
);

const SidebarLogo = ({ src, alt }: { src: string; alt: string }) => (
  <img
    src={src}
    alt={alt}
    style={{
      display: 'block',
      height: '28px',
      maxWidth: '100%',
      objectFit: 'contain',
    }}
  />
);

export function LeftSidebar() {
  if (import.meta.env.MODE === 'e2e' && import.meta.env.VITE_E2E_AUTH_BYPASS === 'true') {
    return <LeftSidebarContent user={null} />;
  }
  return <AuthenticatedLeftSidebar />;
}

function AuthenticatedLeftSidebar() {
  const { user } = useAuthInfo();
  return <LeftSidebarContent user={user} />;
}

function LeftSidebarContent({ user }: { user: User | null | undefined }) {
  const [showAutomationModal, setShowAutomationModal] = useState(false);
  const [showManualRunModal, setShowManualRunModal] = useState(false);

  const { data: automation } = useRetrieveAutomation();

  const [menuOpened, setMenuOpened] = useState(false);

  const { hovered: hoveredSmall, ref: hoverRefSmall } = useHover();
  const { hovered: hoveredLarge, ref: hoverRefLarge } = useHover();

  const [debouncedHoveredSmall] = useDebouncedValue(hoveredSmall, 50);

  const [drawer, setDrawer] = useState({ opened: true, opening: false });

  useEffect(() => {
    if (debouncedHoveredSmall) {
      setDrawer((current) => current.opened ? current : { opened: true, opening: true });
    }
  }, [debouncedHoveredSmall]);

  useEffect(() => {
    if (!hoveredLarge && !menuOpened && !drawer.opening) {
      setDrawer((current) => current.opened ? { ...current, opened: false } : current);
    }
  }, [hoveredLarge, menuOpened, drawer.opening]);

  return (
    <span>
      <Drawer.Root
        opened={drawer.opened}
        onClose={() => {
          setDrawer({ opened: false, opening: false });
        }}
        onEnterTransitionEnd={() => {
          setDrawer((current) => current.opening ? { ...current, opening: false } : current);
        }}
        transitionProps={{ duration: 100, enterDelay: 0 }}
        styles={{ body: { padding: 0 }, content: { maxWidth: '230px' } }}
      >
        <Drawer.Overlay backgroundOpacity={0} blur={0} />
        <Drawer.Content>
          <Stack
            justify={'space-between'}
            w={'100%'}
            h={'100vh'}
            style={{ background: '#fbfbfb', borderRight: '1px solid #e0e0e0' }}
            ref={hoverRefLarge}
          >
            <Stack>
              <Stack px={'sm'} mt={'sm'}>
                <Group>
                  <Link to={'/automations'}>
                    <SidebarLogo src={velosLogoUrl} alt="Velos" />
                  </Link>
                </Group>

                <StyledMenu position="bottom" withinPortal={false}>
                  <Menu.Target>
                    <StyledButton h={'28px'}>
                      <PlusIcon />
                      <Flex ml={'4px'}>Create</Flex>
                    </StyledButton>
                  </Menu.Target>

                  <Menu.Dropdown>
                    <Menu.Item
                      onClick={() => setShowAutomationModal(true)}
                      leftSection={
                        <HiOutlineRectangleGroup size={18} style={{ marginRight: '-4px' }} />
                      }
                    >
                      Create new automation
                    </Menu.Item>
                    <Menu.Item
                      onClick={() => setShowManualRunModal(true)}
                      leftSection={<HiOutlinePlus size={18} style={{ marginRight: '-4px' }} />}
                    >
                      Create manual run
                    </Menu.Item>
                  </Menu.Dropdown>
                </StyledMenu>
              </Stack>
              <Divider mx={'md'} style={{ borderWidth: '0.5px' }} />

              <Stack gap={'4px'}>
                {data.map((item) => (
                  <NavItem label={item.label} link={item.link} Icon={item.Icon} key={item.label} />
                ))}
              </Stack>
            </Stack>
            <Stack>
              {user && (
                <UserMenu user={user} setMenuOpened={setMenuOpened} menuOpened={menuOpened} />
              )}
            </Stack>
          </Stack>
        </Drawer.Content>
      </Drawer.Root>
      {/**/}
      {/**/}
      {/**/}
      <Stack
        justify={'space-between'}
        w={'70px'}
        h={'100vh'}
        style={{ background: '#fbfbfb', borderRight: '1px solid #e0e0e0' }}
        ref={hoverRefSmall}
      >
        <Stack>
          <Stack px={'sm'} mt={'sm'} align={'center'}>
            <Group justify={'center'}>
              <Link to={'/automations'}>
                <SidebarLogo src={velosSmallLogoUrl} alt="Velos" />
              </Link>
            </Group>

            <StyledButton h={'30px'} w={'30px'} aria-label="Create">
              <PlusIcon />
            </StyledButton>
          </Stack>
          <Divider mx={'md'} style={{ borderWidth: '0.5px' }} />

          <Stack gap={'4px'} align={'center'}>
            {data.map((item) => (
              <NavItem label={''} accessibleLabel={item.label} link={item.link} Icon={item.Icon} key={item.label} />
            ))}
          </Stack>
        </Stack>
        <Stack>{user && <UserMenu user={user} includeName={false} />}</Stack>
      </Stack>
      <CreateAutomationModal
        showModal={showAutomationModal}
        setShowModal={setShowAutomationModal}
      />
      <ManualRunModal
        opened={showManualRunModal}
        onClose={() => setShowManualRunModal(false)}
        automation={automation}
      />
    </span>
  );
}
